/**
 * Reports Store
 *
 * Zustand store for Reports feature state management
 */

import {create} from 'zustand';
import {
  Marketer,
  MarketerStoresReport,
  MarketerFinancialReport,
  MarketerStockReport,
  ExportResponse,
  ReportFilters
} from '../models/report.model';

interface ReportsState {
  // Data
  marketers: Marketer[];
  selectedMarketer: Marketer | null;
  storesReport: MarketerStoresReport | null;
  financialReport: MarketerFinancialReport | null;
  stockReport: MarketerStockReport | null;
  exportResponse: ExportResponse | null;

  // Loading states
  isLoadingMarketers: boolean;
  isLoadingStoresReport: boolean;
  isLoadingFinancialReport: boolean;
  isLoadingStockReport: boolean;
  isExporting: boolean;

  // Error states
  error: string | null;

  // Filters
  filters: ReportFilters;

  // Actions
  setMarketers: (marketers: Marketer[]) => void;
  setSelectedMarketer: (marketer: Marketer | null) => void;
  setStoresReport: (report: MarketerStoresReport | null) => void;
  setFinancialReport: (report: MarketerFinancialReport | null) => void;
  setStockReport: (report: MarketerStockReport | null) => void;
  setExportResponse: (response: ExportResponse | null) => void;

  setLoadingMarketers: (loading: boolean) => void;
  setLoadingStoresReport: (loading: boolean) => void;
  setLoadingFinancialReport: (loading: boolean) => void;
  setLoadingStockReport: (loading: boolean) => void;
  setExporting: (exporting: boolean) => void;

  setError: (error: string | null) => void;
  setFilters: (filters: ReportFilters) => void;
  clearFilters: () => void;

  reset: () => void;
}

export const useReportsStore = create<ReportsState>((set) => {
  return {
  // Initial state
  marketers: [],
  selectedMarketer: null,
  storesReport: null,
  financialReport: null,
  stockReport: null,
  exportResponse: null,

  isLoadingMarketers: false,
  isLoadingStoresReport: false,
  isLoadingFinancialReport: false,
  isLoadingStockReport: false,
  isExporting: false,

  error: null,
  filters: {},

  // Actions
  setMarketers: (marketers) => set({marketers}),
  setSelectedMarketer: (marketer) => set({selectedMarketer: marketer}),
  setStoresReport: (report) => set({storesReport: report}),
  setFinancialReport: (report) => set({financialReport: report}),
  setStockReport: (report) => set({stockReport: report}),
  setExportResponse: (response) => set({exportResponse: response}),

  setLoadingMarketers: (loading) => set({isLoadingMarketers: loading}),
  setLoadingStoresReport: (loading) => set({isLoadingStoresReport: loading}),
  setLoadingFinancialReport: (loading) => set({isLoadingFinancialReport: loading}),
  setLoadingStockReport: (loading) => set({isLoadingStockReport: loading}),
  setExporting: (exporting) => set({isExporting: exporting}),

  setError: (error) => set({error}),
  setFilters: (filters) => set({filters}),
  clearFilters: () => set({filters: {}}),

  reset: () => set({
    marketers: [],
    selectedMarketer: null,
    storesReport: null,
    financialReport: null,
    stockReport: null,
    exportResponse: null,
    isLoadingMarketers: false,
    isLoadingStoresReport: false,
    isLoadingFinancialReport: false,
    isLoadingStockReport: false,
    isExporting: false,
    error: null,
    filters: {}
  })
  };
});
